import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { checkEmail, login, signup, verifyCompany, createCompany, forgotPassword, resetPassword, verifyEmail } from '../services/authService';
import { useAuth } from '../context/AuthContext';

function PulseLogoMark() {
  return (
    <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg,#479EC3,#22D3EE)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 20px rgba(34,211,238,0.3)' }}>
      <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
        <polyline points="4,16 8,16 10,9 13,23 16,12 19,20 22,16 28,16"
          stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function DarkInput({ label, ...props }) {
  return (
    <div>
      {label && <label style={{ fontSize: 12.5, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 6, letterSpacing: '0.02em' }}>{label}</label>}
      <input
        className="auth-dark-input"
        style={{
          width: '100%', padding: '12px 15px', borderRadius: 9,
          background: 'rgba(30,41,59,0.8)', border: '1.5px solid rgba(71,158,195,0.2)',
          color: '#e2e8f0', fontSize: 14.5, outline: 'none',
          fontFamily: 'Inter, sans-serif', boxSizing: 'border-box',
          transition: 'border-color .18s, background .18s',
        }}
        onFocus={e => { e.target.style.borderColor = '#479EC3'; e.target.style.background = 'rgba(30,41,59,1)'; }}
        onBlur={e => { e.target.style.borderColor = 'rgba(71,158,195,0.2)'; e.target.style.background = 'rgba(30,41,59,0.8)'; }}
        {...props}
      />
    </div>
  );
}

function EmailBadge({ email }) {
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 9, marginBottom: 14,
      background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(71,158,195,0.15)',
      fontSize: 14, color: '#94a3b8',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <span style={{ color: '#e2e8f0' }}>{email}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    </div>
  );
}

function CompanyBadge({ name, id }) {
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 9, marginBottom: 14,
      background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(71,158,195,0.15)',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#e2e8f0' }}>{name}</span>
        {id && <span style={{ fontSize: 11.5, color: '#64748b' }}>{id}</span>}
      </div>
    </div>
  );
}

function BackButton({ onClick }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: 'none', cursor: 'pointer',
      color: '#64748b', fontSize: 13, display: 'flex', alignItems: 'center',
      gap: 5, marginBottom: 20, padding: 0, transition: 'color .15s',
    }}
      onMouseEnter={e => e.currentTarget.style.color = '#94a3b8'}
      onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
    >
      ← Back
    </button>
  );
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <p style={{ fontSize: 13, color: '#f87171', margin: 0, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
      {msg}
    </p>
  );
}

function SubmitButton({ busy, label, busyLabel }) {
  return (
    <button type="submit" disabled={busy} style={{
      padding: '13px', borderRadius: 10, border: 'none', cursor: 'pointer',
      background: 'linear-gradient(135deg,#479EC3,#22D3EE)', color: '#fff',
      fontSize: 15, fontWeight: 700, boxShadow: '0 0 24px rgba(34,211,238,0.25)',
      transition: 'opacity .2s',
    }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    >{busy ? busyLabel : label}</button>
  );
}

function AuthFieldset({ busy, children }) {
  return (
    <fieldset disabled={busy} style={{
      display: 'flex', flexDirection: 'column', gap: 14,
      border: 'none', margin: 0, padding: 0, minWidth: 0,
      opacity: busy ? 0.7 : 1, transition: 'opacity .2s',
    }}>
      {children}
    </fieldset>
  );
}

const slide = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit:    { opacity: 0, x: -24 },
  transition: { duration: 0.22 },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validateEmail(v) {
  if (!v.trim()) return 'Please enter your email.';
  if (!EMAIL_RE.test(v.trim())) return 'Enter a valid email address.';
  return null;
}

function validatePhone(countryCode, local) {
  if (!local.trim()) return 'Please enter your phone number.';
  const digits = (countryCode + local).replace(/\D/g, '');
  // E.164: 7–15 digits total including country code
  if (digits.length < 7 || digits.length > 15) return 'Enter a valid phone number.';
  return null;
}

function validatePassword(v) {
  if (!v) return 'Please choose a password.';
  if (v.length < 8) return 'Password must be at least 8 characters.';
  if (!/[A-Z]/.test(v)) return 'Include at least one uppercase letter.';
  if (!/[a-z]/.test(v)) return 'Include at least one lowercase letter.';
  if (!/[0-9]/.test(v)) return 'Include at least one number.';
  if (!/[^A-Za-z0-9]/.test(v)) return 'Include at least one special character (!@#$%^&* etc).';
  return null;
}

export default function Auth() {
  const [history, setHistory]     = useState(['choose']);
  const step = history[history.length - 1];
  function goTo(s) { setError(''); setHistory(prev => [...prev, s]); }
  function goBack() { setError(''); setHistory(prev => prev.length > 1 ? prev.slice(0, -1) : prev); }
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [signupPw, setSignupPw]   = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone]         = useState('');
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPw, setConfirmNewPw] = useState('');
  const [verifyCode, setVerifyCode] = useState('');

  const { setLoggedIn } = useAuth();
  const navigate = useNavigate();

  async function handleCompanyId(e) {
    e.preventDefault();
    if (!companyId.trim()) { setError('Please enter your company ID.'); return; }
    setError(''); setBusy(true);
    try {
      const result = await verifyCompany(companyId);
      if (!result.found) { setError('Company ID not found. Please check and try again.'); return; }
      setCompanyName(result.name);
      goTo('email');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleCompanyName(e) {
    e.preventDefault();
    if (!companyName.trim()) { setError('Please enter your company name.'); return; }
    setError(''); setBusy(true);
    try {
      const result = await createCompany(companyName);
      setCompanyId(result.companyId);
      goTo('email');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleEmail(e) {
    e.preventDefault();
    const emailErr = validateEmail(email);
    if (emailErr) { setError(emailErr); return; }
    setError(''); setBusy(true);
    try {
      const { exists } = await checkEmail(email, companyId);
      goTo(exists ? 'login' : 'signup');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    if (!password) { setError('Please enter your password.'); return; }
    setError(''); setBusy(true);
    await new Promise(r => setTimeout(r, 400));
    try { const s = await login(companyId, email, password); setLoggedIn(s); navigate('/dashboard'); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function handleSignup(e) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) { setError('Please enter your full name.'); return; }
    const phoneErr = validatePhone(countryCode, phone);
    if (phoneErr) { setError(phoneErr); return; }
    const pwErr = validatePassword(signupPw);
    if (pwErr) { setError(pwErr); return; }
    if (signupPw !== confirmPw) { setError('Passwords do not match.'); return; }
    setError(''); setBusy(true);
    await new Promise(r => setTimeout(r, 400));
    try {
      const s = await signup({ firstName, lastName, email, password: signupPw, phone: countryCode + phone, companyId, companyName });
      setLoggedIn(s);
      navigate('/dashboard');
    }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await forgotPassword(companyId, email);
      goTo('reset');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    if (!resetCode.trim()) { setError('Please enter the reset code.'); return; }
    const pwErr = validatePassword(newPassword);
    if (pwErr) { setError(pwErr); return; }
    if (newPassword !== confirmNewPw) { setError('Passwords do not match.'); return; }
    setError(''); setBusy(true);
    try {
      await resetPassword(companyId, email, resetCode.trim(), newPassword);
      setResetCode(''); setNewPassword(''); setConfirmNewPw('');
      goTo('login');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function handleVerifyEmail(e) {
    e.preventDefault();
    if (!verifyCode.trim()) { setError('Please enter the verification code.'); return; }
    setError(''); setBusy(true);
    try {
      await verifyEmail(companyId, email, verifyCode.trim());
      goTo('login');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px', position: 'relative', overflow: 'hidden' }}>
      {/* Background glow */}
      <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 400, background: 'radial-gradient(ellipse,rgba(71,158,195,0.12) 0%,transparent 70%)', pointerEvents: 'none' }} />
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        style={{
          width: '100%', maxWidth: 460, position: 'relative', zIndex: 1,
          background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(24px)',
          border: '1px solid rgba(71,158,195,0.2)', borderRadius: 20,
          padding: '44px 40px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
        }}
      >
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', justifyContent: 'center', marginBottom: 36 }}>
          <PulseLogoMark />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: '-.02em' }}>Reano AI Technologies</div>
            <div style={{ fontSize: 11, color: '#479EC3', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Client Portal</div>
          </div>
        </Link>

        <AnimatePresence mode="wait">

          {/* ── Step 1: Choose path ── */}
          {step === 'choose' && (
            <motion.div key="choose" {...slide}>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-.03em', textAlign: 'center', marginBottom: 6 }}>Get Started</h1>
              <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', marginBottom: 32 }}>How would you like to continue?</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button onClick={() => goTo('company-id')} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '18px 20px', borderRadius: 12, border: '1.5px solid rgba(71,158,195,0.25)',
                  background: 'rgba(30,41,59,0.5)', cursor: 'pointer', textAlign: 'left',
                  transition: 'border-color .18s, background .18s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(71,158,195,0.6)'; e.currentTarget.style.background = 'rgba(30,41,59,0.85)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(71,158,195,0.25)'; e.currentTarget.style.background = 'rgba(30,41,59,0.5)'; }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(71,158,195,0.12)', border: '1px solid rgba(71,158,195,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: '#e2e8f0', marginBottom: 2 }}>Existing company login</div>
                    <div style={{ fontSize: 12.5, color: '#64748b' }}>Sign in with your company ID</div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', flexShrink: 0 }}>
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </button>

                <button onClick={() => goTo('company-name')} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '18px 20px', borderRadius: 12, border: '1.5px solid rgba(71,158,195,0.25)',
                  background: 'rgba(30,41,59,0.5)', cursor: 'pointer', textAlign: 'left',
                  transition: 'border-color .18s, background .18s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(71,158,195,0.6)'; e.currentTarget.style.background = 'rgba(30,41,59,0.85)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(71,158,195,0.25)'; e.currentTarget.style.background = 'rgba(30,41,59,0.5)'; }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(71,158,195,0.12)', border: '1px solid rgba(71,158,195,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: '#e2e8f0', marginBottom: 2 }}>New company enrollment</div>
                    <div style={{ fontSize: 12.5, color: '#64748b' }}>Register your company with Reano AI</div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', flexShrink: 0 }}>
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </button>
              </div>
            </motion.div>
          )}

          {/* ── Step 2a: Existing company — enter company ID ── */}
          {step === 'company-id' && (
            <motion.div key="company-id" {...slide}>
              {history.length > 1 && <BackButton onClick={goBack} />}
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-.03em', textAlign: 'center', marginBottom: 6 }}>Company Login</h1>
              <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', marginBottom: 28 }}>Enter your company ID to continue</p>
              <form onSubmit={handleCompanyId}><AuthFieldset busy={busy}>
                <DarkInput autoFocus label="Company ID" type="text" placeholder="e.g. REANO-001" value={companyId} onChange={e => setCompanyId(e.target.value)} />
                <ErrorMsg msg={error} />
                <SubmitButton busy={busy} label="Verify Company →" busyLabel="Verifying…" />
              </AuthFieldset></form>
            </motion.div>
          )}

          {/* ── Step 2b: New company — enter company name ── */}
          {step === 'company-name' && (
            <motion.div key="company-name" {...slide}>
              {history.length > 1 && <BackButton onClick={goBack} />}
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-.03em', textAlign: 'center', marginBottom: 6 }}>New Enrollment</h1>
              <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', marginBottom: 28 }}>Enter your company name to get started</p>
              <form onSubmit={handleCompanyName}><AuthFieldset busy={busy}>
                <DarkInput autoFocus label="Company Name" type="text" placeholder="Your Company Ltd." value={companyName} onChange={e => setCompanyName(e.target.value)} />
                <ErrorMsg msg={error} />
                <SubmitButton busy={busy} label="Create Company →" busyLabel="Setting up…" />
              </AuthFieldset></form>
            </motion.div>
          )}

          {/* ── Step 3: Email ── */}
          {step === 'email' && (
            <motion.div key="email" {...slide}>
              {history.length > 1 && <BackButton onClick={goBack} />}
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-.03em', textAlign: 'center', marginBottom: 6 }}>Welcome</h1>
              <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', marginBottom: 28 }}>Enter your email to sign in or create an account</p>
              <CompanyBadge name={companyName} id={companyId} />
              <form onSubmit={handleEmail}><AuthFieldset busy={busy}>
                <DarkInput autoFocus label="Email" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} />
                <ErrorMsg msg={error} />
                <SubmitButton busy={busy} label="Continue →" busyLabel="Checking…" />
              </AuthFieldset></form>
            </motion.div>
          )}

          {/* ── Step 4a: Login ── */}
          {step === 'login' && (
            <motion.div key="login" {...slide}>
              {history.length > 1 && <BackButton onClick={goBack} />}
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-.03em', textAlign: 'center', marginBottom: 6 }}>Welcome back</h1>
              <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', marginBottom: 28 }}>Sign in to your account</p>
              <CompanyBadge name={companyName} id={companyId} />
              <EmailBadge email={email} />
              <form onSubmit={handleLogin}><AuthFieldset busy={busy}>
                <DarkInput autoFocus label="Password" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} />
                <button type="button" onClick={() => { setError(''); goTo('forgot'); }} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  fontSize: 12.5, color: '#479EC3', textAlign: 'right', fontFamily: 'Inter, sans-serif',
                }}>Forgot password?</button>
                <ErrorMsg msg={error} />
                <SubmitButton busy={busy} label="Sign In" busyLabel="Signing in…" />
              </AuthFieldset></form>
            </motion.div>
          )}

          {/* ── Step 4b: Signup ── */}
          {step === 'signup' && (
            <motion.div key="signup" {...slide}>
              {history.length > 1 && <BackButton onClick={goBack} />}
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-.03em', textAlign: 'center', marginBottom: 6 }}>Create an account</h1>
              <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', marginBottom: 28 }}>Get started with Reano AI today</p>
              <CompanyBadge name={companyName} id={companyId} />
              <EmailBadge email={email} />
              <form onSubmit={handleSignup}><AuthFieldset busy={busy}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <DarkInput label="First Name" type="text" placeholder="First" value={firstName} onChange={e => setFirstName(e.target.value)} />
                  <DarkInput label="Last Name" type="text" placeholder="Last" value={lastName} onChange={e => setLastName(e.target.value)} />
                </div>

                {/* Phone number */}
                <div>
                  <label style={{ fontSize: 12.5, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 6, letterSpacing: '0.02em' }}>Phone Number</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="auth-dark-input"
                      type="text"
                      placeholder="+91"
                      value={countryCode}
                      onChange={e => setCountryCode(e.target.value)}
                      maxLength={5}
                      style={{
                        width: 72, padding: '12px 10px', borderRadius: 9, flexShrink: 0,
                        background: 'rgba(30,41,59,0.8)', border: '1.5px solid rgba(71,158,195,0.2)',
                        color: '#e2e8f0', fontSize: 14.5, outline: 'none', textAlign: 'center',
                        fontFamily: 'Inter, sans-serif', boxSizing: 'border-box',
                        transition: 'border-color .18s, background .18s',
                      }}
                      onFocus={e => { e.target.style.borderColor = '#479EC3'; e.target.style.background = 'rgba(30,41,59,1)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(71,158,195,0.2)'; e.target.style.background = 'rgba(30,41,59,0.8)'; }}
                    />
                    <input
                      className="auth-dark-input"
                      type="tel"
                      placeholder="98765 43210"
                      value={phone}
                      onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                      maxLength={12}
                      style={{
                        flex: 1, padding: '12px 15px', borderRadius: 9,
                        background: 'rgba(30,41,59,0.8)', border: '1.5px solid rgba(71,158,195,0.2)',
                        color: '#e2e8f0', fontSize: 14.5, outline: 'none',
                        fontFamily: 'Inter, sans-serif', boxSizing: 'border-box',
                        transition: 'border-color .18s, background .18s',
                      }}
                      onFocus={e => { e.target.style.borderColor = '#479EC3'; e.target.style.background = 'rgba(30,41,59,1)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(71,158,195,0.2)'; e.target.style.background = 'rgba(30,41,59,0.8)'; }}
                    />
                  </div>
                </div>

                <DarkInput label="Password" type="password" placeholder="••••••••" value={signupPw} onChange={e => setSignupPw(e.target.value)} />
                <DarkInput label="Confirm Password" type="password" placeholder="••••••••" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
                <ErrorMsg msg={error} />
                <SubmitButton busy={busy} label="Create Account" busyLabel="Creating account…" />
              </AuthFieldset></form>
            </motion.div>
          )}

          {/* ── Forgot password ── */}
          {step === 'forgot' && (
            <motion.div key="forgot" {...slide}>
              {history.length > 1 && <BackButton onClick={goBack} />}
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-.03em', textAlign: 'center', marginBottom: 6 }}>Reset Password</h1>
              <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', marginBottom: 28 }}>We'll send a reset code to your email</p>
              <CompanyBadge name={companyName} id={companyId} />
              <EmailBadge email={email} />
              <form onSubmit={handleForgotPassword}><AuthFieldset busy={busy}>
                <ErrorMsg msg={error} />
                <SubmitButton busy={busy} label="Send Reset Code" busyLabel="Sending…" />
              </AuthFieldset></form>
            </motion.div>
          )}

          {/* ── Reset password ── */}
          {step === 'reset' && (
            <motion.div key="reset" {...slide}>
              {history.length > 1 && <BackButton onClick={goBack} />}
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-.03em', textAlign: 'center', marginBottom: 6 }}>Set New Password</h1>
              <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', marginBottom: 28 }}>Enter the code sent to your email</p>
              <CompanyBadge name={companyName} id={companyId} />
              <EmailBadge email={email} />
              <form onSubmit={handleResetPassword}><AuthFieldset busy={busy}>
                <DarkInput autoFocus label="Reset Code" type="text" placeholder="123456" value={resetCode} onChange={e => setResetCode(e.target.value)} />
                <DarkInput label="New Password" type="password" placeholder="••••••••" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                <DarkInput label="Confirm New Password" type="password" placeholder="••••••••" value={confirmNewPw} onChange={e => setConfirmNewPw(e.target.value)} />
                <ErrorMsg msg={error} />
                <SubmitButton busy={busy} label="Reset Password" busyLabel="Resetting…" />
              </AuthFieldset></form>
            </motion.div>
          )}

          {/* ── Verify email ── */}
          {step === 'verify-email' && (
            <motion.div key="verify-email" {...slide}>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-.03em', textAlign: 'center', marginBottom: 6 }}>Verify your email</h1>
              <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', marginBottom: 28 }}>Enter the code we sent to your email</p>
              <EmailBadge email={email} />
              <form onSubmit={handleVerifyEmail}><AuthFieldset busy={busy}>
                <DarkInput autoFocus label="Verification Code" type="text" placeholder="123456" value={verifyCode} onChange={e => setVerifyCode(e.target.value)} />
                <ErrorMsg msg={error} />
                <SubmitButton busy={busy} label="Verify Email" busyLabel="Verifying…" />
              </AuthFieldset></form>
            </motion.div>
          )}

        </AnimatePresence>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 28, paddingTop: 20, textAlign: 'center' }}>
          <Link to="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13.5, color: '#475569', textDecoration: 'none', fontWeight: 500,
            transition: 'color .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = '#94a3b8'}
            onMouseLeave={e => e.currentTarget.style.color = '#475569'}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            Back to Home
          </Link>
        </div>
      </motion.div>

      <style>{`.auth-dark-input::placeholder { color: #475569; }`}</style>
    </div>
  );
}
